import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, Select, SelectItem, Tag } from '@carbon/react';
import { GAME_GROUP_LABEL, type GameGroup } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { CommandStatus } from '../components/labels';
import type { Command } from './Commands';

type Row = {
  id: string; name: string; group: GameGroup; settable: boolean; rtp: string; how: string;
  level: number | null; actual: { rtp: number; stake: number } | null;
};
type Pending = { id: string; status: string; createdAt: string };
type Data = { games: Row[]; min: number; max: number; from: string; to: string; meta: { version: number; source: string; at: string | null }; pending: Pending[] };

const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';
const label = (g: Row, level: number | null) => (level == null ? 'Mặc định (' + g.rtp + ')' : level + '%');

export function GameRtpPage() {
  const { can, me } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['game-rtp'], queryFn: () => api.get<Data>('/api/admin/games/rtp') });
  // id -> level to save (null = back to default); only rows that differ from the server
  const [draft, setDraft] = useState<Map<string, number | null>>(new Map());
  // version the draft was built on, taken at the first edit (see GameLimits)
  const [base, setBase] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/games/rtp', {
      changes: Object.fromEntries(draft), expect: base ?? q.data!.meta.version,
    }),
    onSuccess: (r) => nav('/commands/' + r.command.id),
    onError: (e) => { if (isStale(e)) setSaving(false); },
  });
  const stale = isStale(m.error);
  const discard = () => { setDraft(new Map()); setBase(null); m.reset(); qc.invalidateQueries({ queryKey: ['game-rtp'] }); };

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { games, min, max, from, to, meta, pending } = q.data;
  const byId = new Map(games.map((g) => [g.id, g]));
  // RTP = payout value: founder only (owner 2026-10-04; the API refuses every other role)
  const editable = can('games.config') && me?.role === 'founder';
  const levels: number[] = [];
  for (let l = min; l <= max; l++) levels.push(l);
  const eff = (g: Row) => (draft.has(g.id) ? draft.get(g.id)! : g.level);
  const edit = (g: Row, v: string) => {
    const level = v === '' ? null : Number(v);
    const n = new Map(draft);
    if (level === g.level) n.delete(g.id); else n.set(g.id, level);
    setDraft(n);
    setBase(n.size ? (base ?? meta.version) : null);
  };

  return (
    <div className="page page--with-bar">
      <PageHeader title="RTP game"
        lead={`Tỷ lệ trả thưởng mục tiêu ${min}% đến ${max}% cho từng game. Mức đặt chỉ đổi kết quả ván (đường nổ, xác suất ô, kết quả trả ít hay nhiều cho người chơi thật); không bao giờ đổi bảng trả thưởng hay phí thắng (phí ở Thông số tiền). Tài Xỉu: mức đặt đổi kết quả ván khi nhà cái đỡ phần lệch. RTP không tính tiền hũ.`} />
      <div className="row health">
        <span>Cấu hình: {meta.source === 'default' ? <span className="muted">chưa đặt từ CMS</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
      </div>
      {!editable ? <InlineNotification kind="info" lowContrast hideCloseButton title="Chỉ chủ sở hữu sửa được"
        subtitle="RTP mục tiêu quyết định tiền trả người chơi. Bạn xem được, không sửa được." /> : null}
      {pending.length ? (
        <>
          <InlineNotification kind="info" lowContrast hideCloseButton title={`${pending.length} lệnh đang chờ`}
            subtitle="Đợi lệnh đang gửi xong, hoặc duyệt / từ chối lệnh còn chờ duyệt từ trước, rồi mới tạo lệnh mới: lệnh tạo trên phiên bản cũ sẽ bị hệ A từ chối." />
          <ul className="list pending-list">
            {pending.map((p) => (
              <li key={p.id}><CommandStatus s={p.status} /><Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span></li>
            ))}
          </ul>
        </>
      ) : null}
      {stale ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton
          title="Cài đặt vừa được người khác đổi"
          subtitle={`Bản bạn đang sửa dựa trên phiên bản ${base ?? '?'}, trên máy chủ đã là phiên bản ${meta.version}. Chưa lưu gì. Bỏ bản nháp và tải lại rồi sửa lại.`}
          actionButtonLabel="Bỏ bản nháp và tải lại" onActionButtonClick={discard} />
      ) : null}
      <table className="plain vis">
        <thead><tr><th>Game</th><th>Nhóm</th><th>RTP mặc định</th><th>RTP mục tiêu</th><th className="r">RTP thực 7 ngày</th><th>Mức đặt đổi gì</th></tr></thead>
        <tbody>
          {games.map((g) => (
            <tr key={g.id} className={draft.has(g.id) ? 'is-changed' : undefined}>
              <td className="is-title"><div className="vis__name">{g.name}</div><div className="muted num">{g.id}</div></td>
              <td data-label="Nhóm">{GAME_GROUP_LABEL[g.group]}</td>
              <td data-label="RTP mặc định">{g.rtp}</td>
              <td data-label="RTP mục tiêu">
                {g.settable ? (
                  <Select id={'rtp-' + g.id} className="rtp-select" size="sm" hideLabel labelText={'RTP mục tiêu ' + g.name} disabled={!editable}
                    value={eff(g) == null ? '' : String(eff(g))} onChange={(e) => edit(g, e.target.value)}>
                    <SelectItem value="" text="Mặc định" />
                    {levels.map((l) => <SelectItem key={l} value={String(l)} text={l + '%'} />)}
                  </Select>
                ) : <Tag size="sm" type="gray">chưa chỉnh được</Tag>}
              </td>
              <td className="r" data-label="RTP thực 7 ngày">
                {g.actual ? <><span className="num">{(g.actual.rtp * 100).toFixed(2)}%</span><div className="muted">trên <Money v={g.actual.stake} /></div></> : <span className="muted">-</span>}
              </td>
              <td className="muted" data-label="Mức đặt đổi gì">{g.how}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="note">RTP thực tính từ sổ giao dịch {from} đến {to} (tiền thắng / tiền cược trừ hoàn), gộp mọi mức đã đặt trong khoảng đó; ít cược thì còn dao động mạnh.</p>
      {draft.size ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span><span className="num">{draft.size}</span> game đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={discard}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={stale} onClick={() => setSaving(true)}>Lưu, áp dụng ngay</Button>
        </div>
      ) : null}
      <Modal open={saving} modalHeading={`Đổi RTP ${draft.size} game`} primaryButtonText="Áp dụng ngay" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setSaving(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <table className="plain">
            <thead><tr><th>Game</th><th>Trước</th><th>Sau</th></tr></thead>
            <tbody>
              {[...draft].map(([id, level]) => {
                const g = byId.get(id)!;
                return <tr key={id}><td className="is-title">{g.name}</td><td data-label="Trước">{label(g, g.level)}</td><td data-label="Sau">{label(g, level)}</td></tr>;
              })}
            </tbody>
          </table>
          <p>Thay đổi này đổi số tiền trả cho người chơi. Hệ A áp dụng ngay, không chờ người duyệt, từ ván sau (HiLo: từ lần người chơi mở game sau).</p>
          <InlineError error={stale ? null : m.error} />
        </div>
      </Modal>
    </div>
  );
}
